import { useRef, useState } from "react";
import { Power, ArrowsOut, Keyboard as KeyboardIcon, Info, ArrowClockwise, Gauge, Check, DotsSixVertical, EyeSlash, Eye, Command } from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const QUALITY_SHORT = { auto: "Auto", standard: "1280p", balanced: "1600p", sharp: "HD" };
const QUALITY_LIST = [
  { id: "auto", label: "Auto (adaptive)", sub: "adjusts to your connection" },
  { id: "standard", label: "Bandwidth-friendly", sub: "1280p" },
  { id: "balanced", label: "Balanced", sub: "1600p" },
  { id: "sharp", label: "Sharp (HD)", sub: "1920p" },
];

const POS_KEY = "controlly_toolbar_pos";
const COLLAPSE_KEY = "controlly_toolbar_collapsed";

export function SessionToolbar({ onDisconnect, controlEnabled, onToggleControl, onFullscreen, onClearFrame, panelOpen, onTogglePanel, quality = "auto", effectiveQuality, onSetQuality, onSendKeys }) {
  const containerRef = useRef(null);
  const offset = useRef({ x: 0, y: 0 });
  const [pos, setPos] = useState(() => {
    try { return JSON.parse(localStorage.getItem(POS_KEY)); } catch { return null; }
  });
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === "1");

  const setCollapsedPersist = (v) => {
    setCollapsed(v);
    localStorage.setItem(COLLAPSE_KEY, v ? "1" : "0");
  };

  // Drag from the grip handle. Uses window listeners so the drag keeps working even if the
  // pointer briefly leaves the small handle. Position is clamped to the viewport and remembered.
  const startDrag = (e) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    offset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    const move = (ev) => {
      const w = el.offsetWidth, h = el.offsetHeight;
      let left = Math.max(4, Math.min(ev.clientX - offset.current.x, window.innerWidth - w - 4));
      let top = Math.max(4, Math.min(ev.clientY - offset.current.y, window.innerHeight - h - 4));
      setPos({ left, top });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      setPos((p) => { if (p) localStorage.setItem(POS_KEY, JSON.stringify(p)); return p; });
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    e.preventDefault();
  };

  const posClasses = pos ? "" : "bottom-8 left-1/2 -translate-x-1/2";
  const style = pos ? { left: pos.left, top: pos.top, right: "auto", bottom: "auto", transform: "none" } : undefined;

  const Grip = (
    <button
      data-testid="toolbar-drag-handle"
      onPointerDown={startDrag}
      title="Drag to move"
      className="w-7 h-9 flex items-center justify-center rounded-full text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 transition-colors cursor-move touch-none"
    >
      <DotsSixVertical size={18} weight="bold" />
    </button>
  );

  if (collapsed) {
    return (
      <div
        ref={containerRef}
        style={style}
        data-testid="session-toolbar-collapsed"
        className={`absolute z-50 ${posClasses} backdrop-blur-xl bg-zinc-900/80 border border-white/10 rounded-full px-1.5 py-1.5 flex items-center gap-1 shadow-2xl`}
      >
        {Grip}
        <button
          data-testid="toolbar-expand-button"
          onClick={() => setCollapsedPersist(false)}
          title="Show toolbar"
          className="w-9 h-9 flex items-center justify-center rounded-full text-zinc-300 hover:bg-zinc-800 transition-colors"
        >
          <Eye size={18} />
        </button>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={style}
      data-testid="session-toolbar"
      className={`absolute z-50 ${posClasses} backdrop-blur-xl bg-zinc-900/80 border border-white/10 rounded-full px-2 py-2.5 flex items-center gap-2 shadow-2xl`}
    >
      {Grip}
      <button
        data-testid="disconnect-button"
        onClick={onDisconnect}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-600 hover:bg-red-500 text-white text-sm font-medium transition-colors"
      >
        <Power size={16} weight="bold" /> Disconnect
      </button>
      <div className="w-px h-6 bg-white/10" />
      <button
        data-testid="toggle-control-button"
        onClick={onToggleControl}
        title="Toggle control"
        className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors ${controlEnabled ? "bg-blue-600 text-white hover:bg-blue-500" : "text-zinc-400 hover:bg-zinc-800"}`}
      >
        <KeyboardIcon size={18} weight={controlEnabled ? "fill" : "regular"} />
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="quality-menu-button"
            title="Streaming quality"
            className="h-9 px-3 flex items-center gap-1.5 rounded-full text-zinc-300 hover:bg-zinc-800 transition-colors text-xs font-medium"
          >
            <Gauge size={18} /> <span className="hidden sm:inline">{QUALITY_SHORT[quality]}{quality === "auto" && effectiveQuality ? ` · ${QUALITY_SHORT[effectiveQuality]}` : ""}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" side="top" className="bg-zinc-900 border-white/10 mb-2">
          {QUALITY_LIST.map((o) => (
            <DropdownMenuItem
              key={o.id}
              data-testid={`quality-set-${o.id}`}
              onClick={() => onSetQuality?.(o.id)}
              className="cursor-pointer flex items-center justify-between gap-6"
            >
              <span>
                <span className="text-zinc-200">{o.label}</span>{" "}
                <span className="text-zinc-500 text-xs">· {o.sub}</span>
              </span>
              {quality === o.id && <Check size={14} className="text-blue-400" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <button
        data-testid="fullscreen-button"
        onClick={onFullscreen}
        title="Fullscreen"
        className="w-9 h-9 flex items-center justify-center rounded-full text-zinc-400 hover:bg-zinc-800 transition-colors"
      >
        <ArrowsOut size={18} />
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="sendkeys-menu-button"
            title="Send key combo"
            className="w-9 h-9 flex items-center justify-center rounded-full text-zinc-400 hover:bg-zinc-800 transition-colors"
          >
            <Command size={18} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" side="top" className="bg-zinc-900 border-white/10 mb-2 w-72">
          <DropdownMenuItem data-testid="sendkeys-taskmgr" onClick={() => onSendKeys?.(["ctrl", "shift", "esc"])} className="cursor-pointer flex flex-col items-start gap-0.5">
            <span className="text-zinc-200">Open Task Manager</span>
            <span className="text-zinc-500 text-xs">Ctrl + Shift + Esc</span>
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="sendkeys-lock" onClick={() => onSendKeys?.(["win", "l"])} className="cursor-pointer flex flex-col items-start gap-0.5">
            <span className="text-zinc-200">Lock the computer</span>
            <span className="text-zinc-500 text-xs">Win + L · you'll need its password to return</span>
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="sendkeys-cad" onClick={() => onSendKeys?.(["ctrl", "alt", "delete"])} className="cursor-pointer flex flex-col items-start gap-0.5">
            <span className="text-zinc-200">Ctrl + Alt + Del</span>
            <span className="text-zinc-500 text-xs">best-effort — Windows may ignore it on the secure screen</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <button
        data-testid="refresh-button"
        onClick={onClearFrame}
        title="Clear frame"
        className="w-9 h-9 flex items-center justify-center rounded-full text-zinc-400 hover:bg-zinc-800 transition-colors"
      >
        <ArrowClockwise size={18} />
      </button>
      <button
        data-testid="info-button"
        onClick={onTogglePanel}
        title="Machine info"
        className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors ${panelOpen ? "bg-zinc-700 text-white" : "text-zinc-400 hover:bg-zinc-800"}`}
      >
        <Info size={18} />
      </button>
      <div className="w-px h-6 bg-white/10" />
      <button
        data-testid="toolbar-collapse-button"
        onClick={() => setCollapsedPersist(true)}
        title="Hide toolbar"
        className="w-9 h-9 flex items-center justify-center rounded-full text-zinc-400 hover:bg-zinc-800 transition-colors"
      >
        <EyeSlash size={18} />
      </button>
    </div>
  );
}
